// Pre-submit review row — every item on the order at a glance, side by side,
// directly above the action buttons.
//
// The per-item spec rail (SpecSummary) shows ONE item, and it unmounts when that
// item is collapsed, so a multi-item order — seven books plus the assembly that
// binds them — had nowhere to check the whole thing before sending it for a
// quote. This is the same component in its condensed 'strip' variant: a headline
// read-back per item (see STRIP_KEYS there), not a third full copy of a spec the
// page already carries twice. One card holds the whole row, so the section reads
// as one peer of the order-services card above it rather than as N more cards.
//
// Read-only: nothing here captures or derives a value. The specs come straight
// off each item's stored config, and the only action an item offers is jumping
// back to its configurator.

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssemblySnapshot, FormSchemaDto } from 'shared';

import { SpecSummary } from './SpecSummary.js';
import { deriveProjectName } from './deriveProjectName.js';
import type { FieldValues } from './SchemaForm.js';

export type SummaryStripItem = {
  local_id: string;
  title: string;
  config: FieldValues;
  // The item's live quantity, resolved by the caller (draftQuantity) — it owns
  // the config-vs-column precedence and this must not hold a second opinion.
  quantity: number;
  // How many blocking answers this item is still missing (its share of the
  // line-namespaced fieldErrors map), so the review row flags the item the
  // submit gate will stop on rather than making the customer hunt for it.
  missCount: number;
  // An `assembly` item's kit, shape-checked by the caller — see SpecSummary.
  assembly?: AssemblySnapshot;
};

export function ProjectSummaryStrip({
  schema,
  items,
  quoteTitle,
  isEditable,
  onEdit,
}: {
  schema: FormSchemaDto;
  items: readonly SummaryStripItem[];
  // Order-level name, the last fallback for an item that has neither a typed
  // title nor enough spec to derive one — same precedence as the rail.
  quoteTitle: string;
  // Past editing (submitted / paid / cancelled) the item's configurator reads
  // back its answers instead of taking them, so the card's action says "View".
  isEditable: boolean;
  onEdit: (local_id: string, index: number) => void;
}): JSX.Element | null {
  const { t } = useTranslation('quote');
  const headingId = useId();
  // A single-item order already reads back in its own rail, right beside the
  // form — a second copy of one card would be the same answer twice.
  if (items.length < 2) return null;

  return (
    <section className="card summary-strip" aria-labelledby={headingId}>
      <h3 id={headingId} className="summary-strip__heading">
        {t('summary.strip.heading')}
      </h3>
      <div className="summary-strip__grid">
        {items.map((item, index) => {
          const itemLabel = t('edit.projectN', { n: index + 1 });
          return (
            <div key={item.local_id} className="summary-strip__item">
              <SpecSummary
                schema={schema}
                values={item.config}
                title={item.title || deriveProjectName(schema, item.config) || quoteTitle}
                quantity={item.quantity}
                variant="strip"
                eyebrow={itemLabel}
                {...(item.assembly ? { assembly: item.assembly } : {})}
              />
              <div className="summary-strip__item-foot">
                {/* A column with nothing outstanding still holds the first slot,
                    so the action lands in the same place across the row — the
                    foot is `space-between`, which is what an empty first child is
                    standing in for. */}
                {item.missCount > 0 ? (
                  <span className="summary-strip__flag">
                    {t('edit.collapse.needsAttention', { count: item.missCount })}
                  </span>
                ) : (
                  <span />
                )}
                <button
                  type="button"
                  className="link-button"
                  // N columns carry N identical labels; the accessible name says
                  // which item this one goes to.
                  aria-label={t(isEditable ? 'summary.strip.editAria' : 'summary.strip.viewAria', {
                    item: itemLabel,
                  })}
                  onClick={() => onEdit(item.local_id, index)}
                >
                  {t(isEditable ? 'summary.strip.edit' : 'summary.strip.view')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
